import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, DatabaseTransfer, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Panel, Section, StateView, formatBytes, useAuth, useToast } from "@ozpanel/ui";
import { Plus } from "lucide-react";
import { useCreate } from "../../useCreate";

// Per-tenant resources: databases, database users, cron and panel users. Shapes read from a
// live node and, where a list was empty there, from the Go response structs — noting that the
// REQUEST shape is not always the response shape (posting the wrong key to the alias endpoint
// silently created a catch-all, so this is checked per endpoint, not assumed).

interface DbGrant {
  user: string;
  label: string;
  privilege: string;
}
interface Db {
  name: string;
  label: string;
  user: string;
  engine: string;
  size_bytes: number;
  grants: DbGrant[] | null;
}
interface DbUser {
  user: string;
  label: string;
  engine: string;
  owner: boolean;
  grants: unknown[] | null;
}
interface CronJob {
  id: number;
  schedule: string;
  command: string;
  active: boolean;
}
interface PanelUser {
  id: number;
  email: string;
  role: string;
  status: string;
  created_at: string;
}

// The one-time secret pattern: a generated password exists for exactly one render.
function OneTimeSecret({ label, value, onDismiss }: { label: string; value: string; onDismiss: () => void }) {
  return (
    <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3">
      <div className="text-[12.5px] font-medium">{label}</div>
      <div className="mt-1 break-all font-mono text-[12.5px]">{value}</div>
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => void navigator.clipboard?.writeText(value)}>
          Copy
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
        <span className="text-[11.5px] text-muted">Shown once — the server keeps only a hash.</span>
      </div>
    </Panel>
  );
}

export function DatabasesTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [secret, setSecret] = React.useState<{ label: string; value: string } | null>(null);
  const [confirmDel, setConfirmDel] = React.useState<Db | null>(null);
  const base = `/tenants/${tenantId}/databases`;

  const list = useQuery({
    queryKey: ["tenant-databases", tenantId],
    queryFn: () => client.apiFetch<{ items: Db[] | null; total: number }>(base),
  });

  const create = useCreate({
    title: "Create a database",
    description:
      "The name is prefixed with the account's system user, so it cannot collide with another tenant's. An owner user is created alongside it.",
    submitLabel: "Create database",
    fields: [
      { key: "name", label: "Name", required: true, placeholder: "wordpress" },
      {
        key: "engine",
        label: "Engine",
        type: "enum",
        options: [
          { value: "mysql", label: "MariaDB / MySQL" },
          { value: "postgres", label: "PostgreSQL" },
        ],
        defaultValue: "mysql",
      },
    ],
    run: (v) => client.apiFetch<unknown>(base, { method: "POST", body: JSON.stringify({ name: v.name, engine: v.engine }) }),
    invalidates: [["tenant-databases", tenantId], ["tenant-db-users", tenantId]],
    success: (v) => `${v.name} created`,
  });

  const resetPw = useMutation({
    mutationFn: (d: Db) =>
      client.apiFetch<{ password?: string }>(`${base}/${encodeURIComponent(d.name)}/password`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: (r, d) => {
      if (r?.password) setSecret({ label: `New password for ${d.user}`, value: r.password });
      else notify({ kind: "success", title: "Password reset" });
    },
    onError: (e) => notify({ kind: "error", title: "Reset failed", body: e instanceof Error ? e.message : String(e) }),
  });

  // Every {name}/{user} path param is the FULL name (ozt<uid>_x), not the short label the
  // create form takes. owns() matches on the full name, so sending the label answers
  // "database not found for this tenant: <label>" — which reads like the database is missing
  // rather than like the wrong identifier was sent. Measured on node10: create succeeded with
  // label "v2probe", and grant/revoke/delete against "v2probe" all 404'd while the database
  // existed as ozt100000_v2probe.
  const del = useMutation({
    mutationFn: (d: Db) =>
      client.apiFetch<unknown>(`${base}/${encodeURIComponent(d.name)}`, { method: "DELETE" }),
    onSuccess: (_r, d) => {
      notify({ kind: "success", title: `${d.name} deleted` });
      void qc.invalidateQueries({ queryKey: ["tenant-databases", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<Db>[] = [
    {
      key: "name",
      label: "Database",
      sortable: true,
      // Both names matter: the label is what the customer typed, the full name is what a
      // connection string must use. Showing only one guarantees a support ticket.
      render: (d) => (
        <span>
          <span className="font-mono text-[12.5px]">{d.name}</span>
          {d.label && d.label !== d.name && <span className="ml-2 text-[11.5px] text-muted">“{d.label}”</span>}
        </span>
      ),
    },
    { key: "engine", label: "Engine", sortable: true },
    { key: "user", label: "Owner user", sortable: true, render: (d) => <span className="font-mono text-[12px]">{d.user}</span> },
    { key: "size_bytes", label: "Size", sortable: true, value: (d) => d.size_bytes ?? 0, render: (d) => formatBytes(d.size_bytes) },
    {
      key: "grants",
      label: "Access",
      value: (d) => (d.grants ?? []).length,
      render: (d) => {
        const g = d.grants ?? [];
        return g.length === 0 ? (
          <span className="text-muted">owner only</span>
        ) : (
          <span className="text-[12px]">{g.map((x) => `${x.label || x.user} (${x.privilege})`).join(", ")}</span>
        );
      },
    },
    {
      key: "actions",
      label: "",
      render: (d) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" disabled={resetPw.isPending} onClick={() => resetPw.mutate(d)}>
            Reset password
          </Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmDel(d)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Databases"
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Create database
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        {secret && <OneTimeSecret {...secret} onDismiss={() => setSecret(null)} />}
        <DataList
          items={list.data?.items ?? []}
          columns={columns}
          getRowKey={(d) => d.name}
          loading={list.isLoading}
          error={list.error}
          errorContext="databases"
          emptyMessage="No databases."
        />
        <div className="mt-4"><DatabaseTransfer tenantId={tenantId} databases={list.data?.items ?? []} /></div>
      </div>
      {create.dialog}
      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={`Delete ${confirmDel?.name}?`}
        destructive
        confirmLabel="Delete database"
        pending={del.isPending}
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel);
          setConfirmDel(null);
        }}
        body={
          <p>
            Every table in it is destroyed. Any site still configured to use it starts failing immediately, and the
            data is not recoverable from here — restore from a backup if it turns out to have mattered.
          </p>
        }
      />
    </Section>
  );
}

export function DbUsersTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [secret, setSecret] = React.useState<{ label: string; value: string } | null>(null);
  const [confirmDel, setConfirmDel] = React.useState<DbUser | null>(null);
  const base = `/tenants/${tenantId}/db-users`;

  const list = useQuery({
    queryKey: ["tenant-db-users", tenantId],
    queryFn: () => client.apiFetch<{ items: DbUser[] | null; total: number }>(base),
  });

  const create = useCreate({
    title: "Create a database user",
    description: "A password is generated and shown once. Grant it access to a database from the Databases tab.",
    submitLabel: "Create user",
    fields: [
      // The create body takes `name`; `label` appears only on the RESPONSE. Sending the wrong
      // one is not a validation error about the field — the server sees an empty name and
      // rejects it with a message about the naming RULE, which sends you to check your input
      // rather than your key. Same request-vs-response trap as the mail alias endpoint, where
      // it silently created a domain catch-all instead.
      { key: "name", label: "Name", required: true, placeholder: "app", help: "Lowercase letters, digits and _ (max 32), starting with a letter. Prefixed with the account's system user." },
      {
        key: "engine",
        label: "Engine",
        type: "enum",
        options: [
          { value: "mysql", label: "MariaDB / MySQL" },
          { value: "postgres", label: "PostgreSQL" },
        ],
        defaultValue: "mysql",
      },
    ],
    run: (v) =>
      client
        .apiFetch<{ password?: string; user?: string }>(base, {
          method: "POST",
          body: JSON.stringify({ name: v.name, engine: v.engine }),
        })
        .then((r) => {
          if (r?.password) setSecret({ label: `Password for ${r.user ?? v.name}`, value: r.password });
          return r;
        }),
    invalidates: [["tenant-db-users", tenantId]],
    success: (v) => `${v.name} created`,
  });

  const del = useMutation({
    mutationFn: (u: DbUser) =>
      client.apiFetch<unknown>(`${base}/${encodeURIComponent(u.user)}`, { method: "DELETE" }),
    onSuccess: (_r, u) => {
      notify({ kind: "success", title: `${u.user} deleted` });
      void qc.invalidateQueries({ queryKey: ["tenant-db-users", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<DbUser>[] = [
    { key: "user", label: "User", sortable: true, render: (u) => <span className="font-mono text-[12.5px]">{u.user}</span> },
    { key: "engine", label: "Engine", sortable: true },
    {
      key: "owner",
      label: "Kind",
      value: (u) => (u.owner ? "owner" : "additional"),
      // An owner user is created WITH a database and cannot be deleted on its own; showing it
      // with an enabled Delete button would offer an action that can only fail.
      render: (u) => <StatusPill tone={u.owner ? "accent" : "neutral"}>{u.owner ? "database owner" : "additional"}</StatusPill>,
    },
    {
      key: "actions",
      label: "",
      render: (u) => (
        <div className="flex justify-end">
          <Button size="sm" variant="danger" disabled={u.owner} onClick={() => setConfirmDel(u)}>
            {u.owner ? "Owned by a database" : "Delete"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Database users"
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Create user
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        {secret && <OneTimeSecret {...secret} onDismiss={() => setSecret(null)} />}
        <DataList
          items={list.data?.items ?? []}
          columns={columns}
          getRowKey={(u) => u.user}
          loading={list.isLoading}
          error={list.error}
          errorContext="database users"
          emptyMessage="No database users."
        />
      </div>
      {create.dialog}
      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={`Delete ${confirmDel?.user}?`}
        destructive
        confirmLabel="Delete user"
        pending={del.isPending}
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel);
          setConfirmDel(null);
        }}
        body={<p>Anything connecting as this user stops working immediately. The databases it could reach are untouched.</p>}
      />
    </Section>
  );
}

export function CronTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = React.useState<CronJob | null>(null);
  const base = `/tenants/${tenantId}/cron`;

  const list = useQuery({
    queryKey: ["tenant-cron", tenantId],
    queryFn: () => client.apiFetch<{ items: CronJob[] | null; total: number; user?: string }>(base),
  });

  const create = useCreate({
    title: "Add a scheduled job",
    description: "Runs as the account's own system user. Five fields: minute, hour, day of month, month, day of week.",
    submitLabel: "Add job",
    fields: [
      { key: "schedule", label: "Schedule", required: true, placeholder: "0 3 * * *" },
      { key: "command", label: "Command", required: true, placeholder: "/usr/bin/php /home/…/cron.php" },
    ],
    run: (v) =>
      client.apiFetch<unknown>(base, { method: "POST", body: JSON.stringify({ schedule: v.schedule, command: v.command }) }),
    invalidates: [["tenant-cron", tenantId]],
    success: () => "Scheduled job added",
  });

  const toggle = useMutation({
    mutationFn: (j: CronJob) =>
      client.apiFetch<unknown>(`${base}/${j.id}`, { method: "PUT", body: JSON.stringify({ active: !j.active }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-cron", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Update failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const del = useMutation({
    mutationFn: (j: CronJob) => client.apiFetch<unknown>(`${base}/${j.id}`, { method: "DELETE" }),
    onSuccess: () => {
      notify({ kind: "success", title: "Job deleted" });
      void qc.invalidateQueries({ queryKey: ["tenant-cron", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Delete failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<CronJob>[] = [
    { key: "schedule", label: "Schedule", sortable: true, render: (j) => <span className="font-mono text-[12.5px]">{j.schedule}</span> },
    { key: "command", label: "Command", render: (j) => <span className="break-all font-mono text-[11.5px]">{j.command}</span> },
    {
      key: "active",
      label: "State",
      sortable: true,
      value: (j) => (j.active ? "active" : "paused"),
      render: (j) => <StatusPill tone={j.active ? "ok" : "warn"}>{j.active ? "active" : "paused"}</StatusPill>,
    },
    {
      key: "actions",
      label: "",
      render: (j) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate(j)}>
            {j.active ? "Pause" : "Resume"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setConfirmDel(j)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Section
      title="Scheduled jobs"
      hint={list.data?.user ? `runs as ${list.data.user}` : undefined}
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Add job
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        <DataList
          items={list.data?.items ?? []}
          columns={columns}
          getRowKey={(j) => String(j.id)}
          loading={list.isLoading}
          error={list.error}
          errorContext="scheduled jobs"
          emptyMessage="No scheduled jobs."
        />
      </div>
      {create.dialog}
      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Delete this scheduled job?"
        destructive
        confirmLabel="Delete job"
        pending={del.isPending}
        onConfirm={() => {
          if (confirmDel) del.mutate(confirmDel);
          setConfirmDel(null);
        }}
        body={<p>The job stops running. Pause it instead if you only want to stop it temporarily.</p>}
      />
    </Section>
  );
}

export function PanelUsersTab({ tenantId }: { tenantId: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const base = `/tenants/${tenantId}/users`;

  const list = useQuery({
    queryKey: ["tenant-users", tenantId],
    queryFn: () => client.apiFetch<{ items: PanelUser[] | null; total: number; admins?: number; locked?: number }>(base),
  });

  const invite = useMutation({
    mutationFn: (u: PanelUser) => client.apiFetch<unknown>(`${base}/${u.id}/invite`, { method: "POST" }),
    onSuccess: () => notify({ kind: "success", title: "Invitation sent" }),
    onError: (e) => notify({ kind: "error", title: "Invite failed", body: e instanceof Error ? e.message : String(e) }),
  });

  // The welcome email (POST /tenants/{id}/welcome): the account's console, this login, a one-use link to
  // choose the password, and each domain's website, mail and DNS.
  const welcome = useMutation({
    mutationFn: (u: PanelUser) =>
      client.apiFetch<{ to?: string; note?: string }>(`/tenants/${tenantId}/welcome`, { method: "POST", body: JSON.stringify({ uid: u.id }) }),
    onSuccess: (d) => notify({ kind: "success", title: `Welcome sent to ${d?.to ?? "the login"}`, body: d?.note }),
    onError: (e) => notify({ kind: "error", title: "Welcome not sent", body: e instanceof Error ? e.message : String(e) }),
  });

  // Creating a login and inviting it were already here. Changing one, resetting its password and
  // removing it were not: PUT and DELETE on this path, and POST .../password, were reached by no
  // console, so a login could be made and then never corrected — a wrong role, or somebody who
  // left, could only be fixed with SQL.
  const [pwFor, setPwFor] = React.useState<PanelUser | null>(null);
  const [removing, setRemoving] = React.useState<PanelUser | null>(null);
  const [issued, setIssued] = React.useState<{ email: string; password: string } | null>(null);

  const change = useMutation({
    mutationFn: (v: { u: PanelUser; body: Record<string, unknown> }) =>
      client.apiFetch<unknown>(`${base}/${v.u.id}`, { method: "PUT", body: JSON.stringify(v.body) }),
    onSuccess: () => {
      notify({ kind: "success", title: "Login updated" });
      void qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not update it", body: e instanceof Error ? e.message : String(e) }),
  });

  const resetPw = useMutation({
    // No password sent: the SERVER generates one and returns it once. Better than the console
    // inventing one, because the server's copy was never in a form field.
    mutationFn: (u: PanelUser) =>
      client.apiFetch<{ password?: string }>(`${base}/${u.id}/password`, { method: "POST", body: "{}" }),
    onSuccess: (d, u) => {
      setPwFor(null);
      if (d?.password) setIssued({ email: u.email, password: d.password });
      else notify({ kind: "success", title: "Password reset" });
      void qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not reset it", body: e instanceof Error ? e.message : String(e) }),
  });

  const remove = useMutation({
    mutationFn: (u: PanelUser) => client.apiFetch<unknown>(`${base}/${u.id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      notify({ kind: "success", title: "Login removed" });
      void qc.invalidateQueries({ queryKey: ["tenant-users", tenantId] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not remove it", body: e instanceof Error ? e.message : String(e) }),
  });

  const create = useCreate({
    title: "Add a panel user",
    description: "Gives someone sign-in access to this account's own console. They are emailed an invitation to set a password.",
    submitLabel: "Add user",
    fields: [
      { key: "email", label: "Email", required: true, placeholder: "person@example.com" },
      {
        key: "role",
        label: "Role",
        type: "enum",
        options: [
          { value: "domain_admin", label: "Account admin — full access to this account" },
          { value: "domain_user", label: "Mailbox user — mail only" },
        ],
        defaultValue: "domain_admin",
      },
    ],
    run: (v) => client.apiFetch<unknown>(base, { method: "POST", body: JSON.stringify({ email: v.email, role: v.role }) }),
    invalidates: [["tenant-users", tenantId]],
    success: (v) => `${v.email} added`,
  });

  const columns: DataListColumn<PanelUser>[] = [
    { key: "email", label: "Email", sortable: true },
    { key: "role", label: "Role", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (u) => <StatusPill tone={u.status === "active" ? "ok" : "warn"}>{u.status}</StatusPill>,
    },
    {
      key: "actions",
      label: "",
      render: (u) => (
        <div className="flex flex-wrap justify-end gap-1.5">
          {/* Role is changed in place: it is one of two values and a dialog for a two-way choice
              is a dialog nobody thanks you for. */}
          <select
            value={u.role ?? "domain_user"}
            onChange={(e) => change.mutate({ u, body: { role: e.target.value } })}
            className="rounded-lg border border-line2 bg-bg px-2 py-1 text-[12px] text-fg"
          >
            <option value="domain_admin">account admin</option>
            <option value="domain_user">mailbox user</option>
          </select>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => change.mutate({ u, body: { status: u.status === "active" ? "disabled" : "active", unlock: true } })}
          >
            {u.status === "active" ? "disable" : "enable"}
          </Button>
          <Button size="sm" variant="ghost" disabled={invite.isPending} onClick={() => invite.mutate(u)}>
            invite
          </Button>
          {u.role === "domain_admin" && (
            <Button size="sm" variant="ghost" disabled={welcome.isPending} onClick={() => welcome.mutate(u)}>
              welcome
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setPwFor(u)}>
            reset password
          </Button>
          <Button size="sm" variant="danger" onClick={() => setRemoving(u)}>
            remove
          </Button>
        </div>
      ),
    },
  ];

  const userDialogs = (
    <>
      <ConfirmDialog
        open={!!pwFor}
        onOpenChange={(o) => !o && setPwFor(null)}
        title={pwFor ? `Reset the password for ${pwFor.email}?` : ""}
        confirmLabel="Reset it"
        body={
          <p>
            A new password is generated and shown once. Their existing sessions are revoked, so
            they are signed out everywhere and must use the new one. Sending them an invitation
            instead lets them choose their own and never puts a password through you.
            {pwFor?.status !== "active" && (
              <>
                {" "}
                <strong>
                  This login is currently disabled, and resetting its password ENABLES it again —
                  measured on a live node, not inferred. Disable it once more afterwards if it is
                  meant to stay shut.
                </strong>
              </>
            )}
          </p>
        }
        onConfirm={() => pwFor && resetPw.mutate(pwFor)}
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing ? `Remove the login ${removing.email}?` : ""}
        confirmLabel="Remove it"
        destructive
        body={
          <p>
            This removes their way IN. The mailbox and its mail are untouched — if this person had
            a mailbox it keeps receiving, and mail is deleted from the mail screens, never here.
          </p>
        }
        onConfirm={() => removing && remove.mutate(removing)}
      />
      <ConfirmDialog
        open={!!issued}
        onOpenChange={(o) => !o && setIssued(null)}
        title="New password"
        confirmLabel="Done"
        body={
          <div className="text-[12.5px]">
            <p>
              Shown once — it is not stored anywhere it can be read back. Give it to{" "}
              {issued?.email} over something other than email.
            </p>
            <p className="mt-2 select-all rounded-lg border border-line bg-bg2 px-3 py-2 font-mono">
              {issued?.password}
            </p>
          </div>
        }
        onConfirm={() => setIssued(null)}
      />
    </>
  );

  return (
    <Section
      title="Panel users"
      hint={list.data ? `${list.data.total} user(s), ${list.data.admins ?? 0} admin(s)` : undefined}
      actions={
        <Button size="sm" variant="accent" onClick={create.open}>
          <Plus size={14} /> Add user
        </Button>
      }
      bodyClassName="p-0"
    >
      <div className="p-4">
        <StateView loading={list.isLoading} error={list.error} errorContext="panel users">
          <>
            {(list.data?.locked ?? 0) > 0 && (
              <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                {list.data?.locked} user(s) are locked out and cannot sign in.
              </Panel>
            )}
            <DataList
              items={list.data?.items ?? []}
              columns={columns}
              getRowKey={(u) => String(u.id)}
              emptyMessage="No panel users on this account."
              emptyHint="Without one, nobody can sign in to manage this account."
            />
          </>
        </StateView>
      </div>
      {create.dialog}
      {userDialogs}
    </Section>
  );
}
